import { ClockClockwiseIcon, QueueIcon, StopIcon, WavesIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import deviceRunningBg from '@/assets/images/device-running-bg.webp';
import deviceStoppedBg from '@/assets/images/device-stopped-bg.webp';
import { routes } from '@/config/routes';

import { useDeviceCommand } from '../command-store';
import {
  DISABLED_TAG,
  cardStatus,
  deviceTypeLabel,
  disabledReason,
  formatLastSynced,
  hasPowerControl,
} from '../format';
import type { Device } from '../types';

import styles from './device-card.module.scss';

export type DeviceCardProps = {
  device: Device;
  /** Circular power On/Off control (Slice 1). Independent tap area — does not open detail. */
  powerControl?: ReactNode;
  /** Pinned "now" for stable relative timestamps (optional). */
  now?: number;
  /** Launch the device-configuration flow for an unconfigured device (story 101971). */
  onFinishSetup?: (device: Device) => void;
};

/**
 * Device card (story 101847 / Figma device card). Type + last-synced header, large name,
 * product image with a Running/Stopped/Offline status pill, and a circular power control
 * bottom-right. Devices with a power control render the Figma background image (a lime
 * wash + a notch the button nests into) behind the content; running vs. stopped/offline
 * picks which of the two pre-rendered images is used.
 *
 * Unconfigured devices (story 101971) are a distinct, full-colour "Setup Required" card with
 * a Finish Setup CTA that launches configuration. Other disabled devices show only their
 * status tag, are dimmed, and are non-interactive.
 */
export function DeviceCard({ device, powerControl, now, onFinishSetup }: DeviceCardProps) {
  const navigate = useNavigate();
  const disabled = disabledReason(device);
  const isSetup = disabled === 'not_configured';
  const lastSynced = formatLastSynced(device.lastSyncedAt, now);
  const command = useDeviceCommand(device.id);
  const status = cardStatus(device);
  const toneClass =
    status.tone === 'running'
      ? styles.toneRunning
      : status.tone === 'stopped'
        ? styles.toneStopped
        : styles.toneOffline;
  // Figma's card background (with its baked-in power-button notch) only makes sense
  // under an actual power button — non-power devices keep the plain card.
  const cardBg = hasPowerControl(device)
    ? status.tone === 'running'
      ? deviceRunningBg
      : deviceStoppedBg
    : null;
  // Devices without a power control (Diesel pumps, sensors) get the original CSS
  // gradient wash when running instead of the image (there's no button/notch for them).
  const legacyRunningWash = !cardBg && !disabled && status.tone === 'running';

  // Setup cards are interactive (launch config); other disabled cards are not.
  const interactive = isSetup || !disabled;

  const open = () => {
    if (isSetup) {
      onFinishSetup?.(device);
      return;
    }
    if (!disabled) navigate(routes.device(device.id));
  };

  return (
    <div
      className={[
        styles.card,
        cardBg ? styles.hasBg : undefined,
        legacyRunningWash ? styles.cardRunning : undefined,
        disabled && !isSetup ? styles.disabled : undefined,
      ]
        .filter(Boolean)
        .join(' ')}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-disabled={interactive ? undefined : true}
      onClick={open}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          open();
        }
      }}
    >
      {cardBg ? <img src={cardBg} alt="" className={styles.cardBg} /> : null}

      <div className={styles.content}>
        <div className={styles.head}>
          <span className={styles.type}>{deviceTypeLabel(device)}</span>
          {isSetup ? (
            <span className={styles.setupBadge}>Setup Required</span>
          ) : lastSynced ? (
            <span className={styles.time}>
              <ClockClockwiseIcon size={14} weight="bold" />
              {lastSynced}
            </span>
          ) : null}
        </div>

        <h3 className={styles.name}>{device.name}</h3>

        <div className={styles.media}>
          <img src={device.imageUrl} alt={device.name} className={styles.image} loading="lazy" />
          {isSetup ? (
            <button
              type="button"
              className={styles.finishSetup}
              onClick={(e) => {
                e.stopPropagation();
                onFinishSetup?.(device);
              }}
            >
              Finish Setup
            </button>
          ) : disabled ? (
            <span
              className={`${styles.pill} ${disabled === 'pending_activation' ? styles.tonePending : styles.toneOffline}`}
            >
              {DISABLED_TAG[disabled]}
            </span>
          ) : command ? (
            <span className={`${styles.pill} ${styles.toneQueued}`}>
              Queued
              <QueueIcon size={14} weight="bold" />
            </span>
          ) : (
            <span className={`${styles.pill} ${toneClass}`}>
              {status.text}
              {status.tone === 'running' ? (
                <WavesIcon size={14} weight="bold" />
              ) : status.tone === 'stopped' ? (
                <StopIcon size={12} weight="fill" />
              ) : null}
            </span>
          )}
        </div>

        {/* Power control is its own tap target; stop propagation so it never opens detail. */}
        {powerControl ? (
          <div
            className={styles.action}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            {powerControl}
          </div>
        ) : null}
      </div>
    </div>
  );
}
